import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Industry } from "./types";
import Header from "../../components/ui/Header";
import IndustrySelector from "./components/IndustrySelector";
import IndustryHero from "./components/IndustryHero";
import UseCaseGrid from "./components/UseCaseGrid";
import MetricsShowcase from "./components/MetricsShowcase";
import CaseStudySection from "./components/CaseStudySection";
import ResourceLibrary from "./components/ResourceLibrary";
import ComplianceSection from "./components/ComplianceSection";
import ROICalculator from "./components/ROICalculator";
import CTASection from "./components/CTASection";

const IndustryApplications = () => {
  const [activeIndustry, setActiveIndustry] = useState<string>("retail");
  const [showROICalculator, setShowROICalculator] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeIndustry]);

  const industries: Industry[] = [
    {
      id: "retail",
      name: "Retail & Commerce",
      slug: "retail",
      icon: "ShoppingBag",
      description: `Transform your retail operations with AI-powered analytics that deliver actionable insights into customer behavior, store performance, and operational efficiency. Our platform helps retail chains optimize layouts, reduce shrinkage, and increase conversion rates through intelligent video analysis.`,
      heroImage: "https://images.unsplash.com/photo-1586965142106-83751d8f57a3",
      heroImageAlt:
        "Modern retail store interior with customers shopping and bright lighting showcasing product displays",
      tagline: "Elevate Retail Intelligence with AI-Powered Analytics",
      keyBenefits: [
        "Increase conversion rates by 15-25% through customer journey optimization",
        "Reduce shrinkage and loss prevention costs by up to 40%",
        "Optimize staff allocation based on real-time footfall analytics",
        "Enhance customer experience with queue management and heat mapping",
      ],

      useCases: [
        {
          id: "footfall",
          title: "Footfall Analytics",
          description:
            "Track customer traffic patterns, peak hours, and zone popularity to optimize store layouts and staffing.",
          icon: "Users",
          features: [
            "Real-time visitor counting",
            "Demographic analysis",
            "Dwell time measurement",
            "Entry/exit tracking",
          ],
        },
        {
          id: "queue",
          title: "Queue Management",
          description:
            "Monitor checkout lines and service counters to reduce wait times and improve customer satisfaction.",
          icon: "Clock",
          features: [
            "Wait time analytics",
            "Staff allocation alerts",
            "Customer flow optimization",
            "Service level monitoring",
          ],
        },
        {
          id: "heatmap",
          title: "Heat Mapping",
          description:
            "Visualize customer movement and engagement zones to optimize product placement and store design.",
          icon: "Map",
          features: [
            "Zone popularity analysis",
            "Product engagement tracking",
            "Layout optimization insights",
            "Conversion funnel visualization",
          ],
        },
        {
          id: "loss",
          title: "Loss Prevention",
          description:
            "Detect suspicious behavior and unusual patterns to prevent theft and reduce inventory shrinkage.",
          icon: "Shield",
          features: [
            "Anomaly detection",
            "Suspicious activity alerts",
            "Inventory monitoring",
            "Incident documentation",
          ],
        },
        {
          id: "staff",
          title: "Staff Performance",
          description:
            "Analyze employee productivity and customer service quality to improve operational efficiency.",
          icon: "UserCheck",
          features: [
            "Service time tracking",
            "Customer interaction analysis",
            "Productivity metrics",
            "Training needs identification",
          ],
        },
        {
          id: "conversion",
          title: "Conversion Analytics",
          description:
            "Measure and optimize the customer journey from entry to purchase with detailed conversion metrics.",
          icon: "TrendingUp",
          features: [
            "Conversion rate tracking",
            "Basket abandonment analysis",
            "Product interest measurement",
            "Purchase behavior insights",
          ],
        },
      ],

      metrics: [
        {
          label: "Average Conversion Increase",
          value: "18.5%",
          change: "+12%",
          trend: "up",
          icon: "TrendingUp",
        },
        {
          label: "Shrinkage Reduction",
          value: "35%",
          change: "+28%",
          trend: "up",
          icon: "Shield",
        },
        {
          label: "Customer Satisfaction",
          value: "92%",
          change: "+15%",
          trend: "up",
          icon: "Heart",
        },
        {
          label: "Operational Efficiency",
          value: "40%",
          change: "+25%",
          trend: "up",
          icon: "Zap",
        },
      ],

      caseStudies: [
        {
          id: "retail-cs-1",
          companyName: "GlobalMart Retail Chain",
          industry: "Multi-format Retail",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_186366857-1764487568559.png",
          logoAlt: "GlobalMart retail chain logo with shopping cart icon",
          challenge: `GlobalMart operated 150+ stores across multiple formats but struggled with inconsistent customer experiences, high shrinkage rates, and inefficient staff allocation. They lacked visibility into customer behavior patterns and couldn't optimize store layouts based on data.`,
          solution: `Implemented camlytix AI across all locations with footfall analytics, heat mapping, queue management, and loss prevention modules. Integrated with existing POS and inventory systems for comprehensive insights.`,
          results: [
            {
              metric: "Conversion Rate",
              value: "+22%",
              description: "Increase in store conversion",
            },
            {
              metric: "Shrinkage",
              value: "-38%",
              description: "Reduction in inventory loss",
            },
            {
              metric: "Customer Satisfaction",
              value: "+28%",
              description: "Improvement in CSAT scores",
            },
          ],

          testimonialQuote: `camlytix AI transformed how we understand and serve our customers. The insights we gained led to a complete reimagining of our store operations, resulting in significant improvements across all key metrics.`,
          testimonialAuthor: "Sarah Mitchell",
          testimonialRole: "VP of Retail Operations",
          image: "https://images.unsplash.com/photo-1644310588055-8dd33d471e47",
          imageAlt:
            "Busy retail store with customers browsing products and staff assisting shoppers",
        },
        {
          id: "retail-cs-2",
          companyName: "Fashion Forward Boutiques",
          industry: "Fashion Retail",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_14175314c-1764487569273.png",
          logoAlt:
            "Fashion Forward boutiques elegant logo with dress silhouette",
          challenge: `Fashion Forward's 45 boutique locations faced challenges with understanding customer preferences, optimizing product placement, and managing peak hour staffing. Traditional analytics couldn't capture the nuanced behavior of fashion shoppers.`,
          solution: `Deployed comprehensive analytics suite including demographic analysis, dwell time tracking, and product engagement metrics. Created custom dashboards for merchandising and operations teams.`,
          results: [
            {
              metric: "Sales per Sq Ft",
              value: "+31%",
              description: "Increase in space productivity",
            },
            {
              metric: "Staff Efficiency",
              value: "+42%",
              description: "Improvement in labor utilization",
            },
            {
              metric: "Customer Engagement",
              value: "+55%",
              description: "Increase in product interactions",
            },
          ],

          testimonialQuote: `The platform gave us unprecedented visibility into how customers interact with our merchandise. We've completely transformed our merchandising strategy based on these insights, leading to record-breaking sales.`,
          testimonialAuthor: "Michael Chen",
          testimonialRole: "Chief Merchandising Officer",
          image: "https://images.unsplash.com/photo-1621261027519-a71ac66d5a68",
          imageAlt:
            "Elegant fashion boutique interior with clothing displays and customers trying on garments",
        },
      ],

      resources: [
        {
          id: "retail-wp-1",
          title: "Retail Analytics Best Practices",
          type: "whitepaper",
          description:
            "Comprehensive guide to implementing AI-powered analytics in retail environments",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "retail-guide-1",
          title: "Store Optimization Playbook",
          type: "guide",
          description:
            "Step-by-step implementation guide for maximizing retail performance",
          downloadUrl: "#",
          icon: "BookOpen",
        },
        {
          id: "retail-checklist-1",
          title: "Loss Prevention Checklist",
          type: "checklist",
          description:
            "Essential checklist for reducing shrinkage and improving security",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
        {
          id: "retail-report-1",
          title: "Retail Industry Benchmark Report",
          type: "report",
          description:
            "Latest trends and performance benchmarks in retail analytics",
          downloadUrl: "#",
          icon: "BarChart3",
        },
      ],

      testimonial: {
        quote: `camlytix AI has revolutionized how we understand customer behavior. The insights have directly contributed to a 25% increase in conversion rates across our store network.`,
        author: "Jennifer Martinez",
        role: "Director of Retail Operations",
        company: "Premium Retail Group",
        image:
          "https://img.rocket.new/generatedImages/rocket_gen_img_1a9e8814c-1763296696290.png",
        imageAlt:
          "Professional woman with brown hair in business attire smiling at camera",
      },
      complianceInfo: {
        title: "Privacy & Compliance",
        description:
          "Our retail analytics solutions are built with privacy-first principles and comply with all major data protection regulations.",
        certifications: [
          {
            name: "GDPR Compliant",
            icon: "Shield",
            description: "Full compliance with EU data protection regulations",
          },
          {
            name: "CCPA Certified",
            icon: "FileCheck",
            description: "California Consumer Privacy Act compliance",
          },
          {
            name: "ISO 27001",
            icon: "Award",
            description: "Information security management certification",
          },
          {
            name: "SOC 2 Type II",
            icon: "Lock",
            description: "Security and availability controls verified",
          },
        ],

        regulations: [
          "Anonymized data processing with no personally identifiable information storage",
          "Configurable data retention policies aligned with regional requirements",
          "Transparent opt-out mechanisms for customers",
          "Regular third-party security audits and penetration testing",
          "Encrypted data transmission and storage with industry-standard protocols",
        ],
      },
    },
    {
      id: "traffic",
      name: "Traffic & Smart Cities",
      slug: "traffic",
      icon: "Car",
      description: `Revolutionize urban traffic management with AI-powered analytics that optimize traffic flow, reduce congestion, and enhance public safety. Our ATCC (Adaptive Traffic Control Center) solutions help cities implement intelligent transportation systems that adapt in real-time to changing conditions.`,
      heroImage: "https://images.unsplash.com/photo-1725088433636-18c2ea0d0db3",
      heroImageAlt:
        "Aerial view of busy city intersection with multiple lanes of traffic and pedestrian crossings",
      tagline: "Build Smarter Cities with Intelligent Traffic Analytics",
      keyBenefits: [
        "Reduce traffic congestion by up to 35% through adaptive signal control",
        "Decrease accident rates by 45% with predictive safety analytics",
        "Lower carbon emissions by 25% through optimized traffic flow",
        "Improve emergency response times with real-time incident detection",
      ],

      useCases: [
        {
          id: "atcc",
          title: "Adaptive Traffic Control",
          description:
            "Dynamically adjust signal timing based on real-time traffic conditions to optimize flow and reduce congestion.",
          icon: "TrafficCone",
          features: [
            "Real-time signal optimization",
            "Predictive traffic modeling",
            "Corridor coordination",
            "Emergency vehicle prioritization",
          ],
        },
        {
          id: "incident",
          title: "Incident Detection",
          description:
            "Automatically detect accidents, breakdowns, and unusual events to enable rapid response and minimize disruption.",
          icon: "AlertTriangle",
          features: [
            "Automatic incident detection",
            "Wrong-way driver alerts",
            "Stopped vehicle identification",
            "Emergency notification system",
          ],
        },
        {
          id: "violation",
          title: "Violation Monitoring",
          description:
            "Identify traffic violations including red light running, illegal turns, and speed violations for enforcement.",
          icon: "Camera",
          features: [
            "Red light violation detection",
            "Speed monitoring",
            "Lane violation tracking",
            "Evidence documentation",
          ],
        },
        {
          id: "pedestrian",
          title: "Pedestrian Safety",
          description:
            "Monitor crosswalks and pedestrian zones to enhance safety and optimize signal timing for foot traffic.",
          icon: "Users",
          features: [
            "Crosswalk monitoring",
            "Pedestrian counting",
            "Safety zone alerts",
            "Adaptive crossing signals",
          ],
        },
        {
          id: "parking",
          title: "Smart Parking",
          description:
            "Track parking availability and guide drivers to open spaces, reducing search time and congestion.",
          icon: "ParkingCircle",
          features: [
            "Real-time availability tracking",
            "Occupancy analytics",
            "Violation detection",
            "Revenue optimization",
          ],
        },
        {
          id: "analytics",
          title: "Traffic Analytics",
          description:
            "Comprehensive traffic data analysis for urban planning, infrastructure development, and policy decisions.",
          icon: "BarChart3",
          features: [
            "Volume and speed analysis",
            "Origin-destination studies",
            "Peak hour identification",
            "Long-term trend analysis",
          ],
        },
      ],

      metrics: [
        {
          label: "Congestion Reduction",
          value: "32%",
          change: "+18%",
          trend: "up",
          icon: "TrendingDown",
        },
        {
          label: "Accident Prevention",
          value: "42%",
          change: "+35%",
          trend: "up",
          icon: "Shield",
        },
        {
          label: "Emission Reduction",
          value: "28%",
          change: "+22%",
          trend: "up",
          icon: "Leaf",
        },
        {
          label: "Response Time",
          value: "-45%",
          change: "+38%",
          trend: "up",
          icon: "Clock",
        },
      ],

      caseStudies: [
        {
          id: "traffic-cs-1",
          companyName: "Metro City Transportation Authority",
          industry: "Urban Traffic Management",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1520c6071-1764487567920.png",
          logoAlt:
            "Metro City Transportation Authority logo with traffic light symbol",
          challenge: `Metro City faced severe congestion during peak hours, high accident rates at major intersections, and inefficient emergency vehicle routing. Traditional fixed-timing signals couldn't adapt to varying traffic patterns throughout the day.`,
          solution: `Deployed comprehensive ATCC solution across 250 intersections with adaptive signal control, incident detection, and predictive analytics. Integrated with emergency services dispatch system for priority routing.`,
          results: [
            {
              metric: "Travel Time",
              value: "-28%",
              description: "Reduction in average commute",
            },
            {
              metric: "Accidents",
              value: "-41%",
              description: "Decrease in intersection incidents",
            },
            {
              metric: "Emissions",
              value: "-32%",
              description: "Reduction in vehicle emissions",
            },
          ],

          testimonialQuote: `The adaptive traffic control system has transformed our city's transportation infrastructure. We've seen dramatic improvements in traffic flow and safety, while also reducing our environmental impact.`,
          testimonialAuthor: "David Thompson",
          testimonialRole: "Director of Transportation",
          image: "https://images.unsplash.com/photo-1673622533973-b8d60eab4151",
          imageAlt:
            "Busy urban intersection with multiple traffic signals and vehicles moving through coordinated lanes",
        },
        {
          id: "traffic-cs-2",
          companyName: "Coastal Highway Authority",
          industry: "Highway Management",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1dea8d070-1764487569242.png",
          logoAlt:
            "Coastal Highway Authority logo with highway and ocean wave design",
          challenge: `Managing a 150-mile coastal highway corridor with high tourist traffic, frequent accidents, and limited visibility during adverse weather conditions. Manual monitoring couldn't provide adequate coverage.`,
          solution: `Implemented intelligent highway monitoring system with incident detection, weather-adaptive controls, and automated emergency response coordination across the entire corridor.`,
          results: [
            {
              metric: "Incident Response",
              value: "-52%",
              description: "Faster emergency response",
            },
            {
              metric: "Safety Rating",
              value: "+65%",
              description: "Improvement in safety metrics",
            },
            {
              metric: "Traffic Flow",
              value: "+38%",
              description: "Increase in throughput",
            },
          ],

          testimonialQuote: `The system's ability to detect incidents immediately and coordinate emergency response has been life-saving. We've dramatically improved safety while maintaining smooth traffic flow even during peak tourist season.`,
          testimonialAuthor: "Maria Rodriguez",
          testimonialRole: "Chief Operations Officer",
          image: "https://images.unsplash.com/photo-1581023690496-397f8829b39b",
          imageAlt:
            "Scenic coastal highway with multiple lanes of traffic and ocean view in background",
        },
      ],

      resources: [
        {
          id: "traffic-wp-1",
          title: "Smart City Traffic Management",
          type: "whitepaper",
          description:
            "Comprehensive guide to implementing intelligent traffic systems",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "traffic-guide-1",
          title: "ATCC Implementation Guide",
          type: "guide",
          description:
            "Step-by-step deployment guide for adaptive traffic control",
          downloadUrl: "#",
          icon: "BookOpen",
        },
        {
          id: "traffic-checklist-1",
          title: "Safety Compliance Checklist",
          type: "checklist",
          description: "Essential requirements for traffic safety systems",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
        {
          id: "traffic-report-1",
          title: "Urban Mobility Trends Report",
          type: "report",
          description: "Latest insights on smart city transportation",
          downloadUrl: "#",
          icon: "BarChart3",
        },
      ],

      testimonial: {
        quote: `Our city's traffic management has been completely transformed. The AI-powered analytics have reduced congestion by 35% and significantly improved air quality.`,
        author: "Robert Chen",
        role: "City Traffic Commissioner",
        company: "Metropolitan Transportation Department",
        image:
          "https://img.rocket.new/generatedImages/rocket_gen_img_183fc715d-1763301362797.png",
        imageAlt:
          "Professional Asian man in suit with glasses smiling confidently",
      },
      complianceInfo: {
        title: "Regulatory Compliance",
        description:
          "Our traffic management solutions comply with all federal and state transportation regulations and privacy requirements.",
        certifications: [
          {
            name: "FHWA Approved",
            icon: "Shield",
            description: "Federal Highway Administration compliance",
          },
          {
            name: "MUTCD Compliant",
            icon: "FileCheck",
            description: "Manual on Uniform Traffic Control Devices",
          },
          {
            name: "ITS Standards",
            icon: "Award",
            description: "Intelligent Transportation Systems certification",
          },
          {
            name: "Privacy Certified",
            icon: "Lock",
            description: "No license plate or facial data storage",
          },
        ],

        regulations: [
          "Full compliance with federal transportation safety standards",
          "Adherence to state and local traffic management regulations",
          "Privacy-preserving analytics with no personal data collection",
          "Regular system audits and performance validation",
          "Integration with existing emergency response protocols",
        ],
      },
    },
    {
      id: "enterprise",
      name: "Enterprise Security",
      slug: "enterprise",
      icon: "Building2",
      description: `Enhance enterprise security and operational efficiency with AI-powered video analytics that provide comprehensive situational awareness, threat detection, and business intelligence. Our platform transforms traditional surveillance into a proactive security and operations management system.`,
      heroImage: "https://images.unsplash.com/photo-1583916832932-a658eeb1199d",
      heroImageAlt:
        "Modern corporate office building exterior with glass facade and security entrance",
      tagline: "Secure Your Enterprise with Intelligent Analytics",
      keyBenefits: [
        "Reduce security incidents by 60% through proactive threat detection",
        "Improve operational efficiency by 45% with automated monitoring",
        "Decrease false alarms by 75% using AI-powered verification",
        "Enhance compliance with automated audit trails and reporting",
      ],

      useCases: [
        {
          id: "perimeter",
          title: "Perimeter Security",
          description:
            "Monitor facility boundaries with intelligent intrusion detection and automated alert systems.",
          icon: "Shield",
          features: [
            "Intrusion detection",
            "Loitering alerts",
            "Fence line monitoring",
            "Unauthorized access detection",
          ],
        },
        {
          id: "access",
          title: "Access Control",
          description:
            "Integrate with access systems to verify authorized entry and detect tailgating or unauthorized access.",
          icon: "Key",
          features: [
            "Tailgating detection",
            "Badge verification",
            "Restricted area monitoring",
            "Entry/exit logging",
          ],
        },
        {
          id: "crowd",
          title: "Crowd Management",
          description:
            "Monitor occupancy levels and crowd density to ensure safety and optimize space utilization.",
          icon: "Users",
          features: [
            "Occupancy counting",
            "Density monitoring",
            "Evacuation assistance",
            "Social distancing compliance",
          ],
        },
        {
          id: "asset",
          title: "Asset Protection",
          description:
            "Track valuable assets and equipment with automated monitoring and theft prevention.",
          icon: "Package",
          features: [
            "Asset tracking",
            "Removal detection",
            "Inventory monitoring",
            "Chain of custody logging",
          ],
        },
        {
          id: "safety",
          title: "Workplace Safety",
          description:
            "Detect safety violations and hazardous conditions to prevent accidents and ensure compliance.",
          icon: "AlertTriangle",
          features: [
            "PPE compliance monitoring",
            "Hazard zone detection",
            "Slip and fall prevention",
            "Emergency response coordination",
          ],
        },
        {
          id: "operations",
          title: "Operations Intelligence",
          description:
            "Gain insights into facility usage, workflow efficiency, and resource optimization.",
          icon: "BarChart3",
          features: [
            "Space utilization analysis",
            "Workflow optimization",
            "Resource allocation insights",
            "Performance metrics tracking",
          ],
        },
      ],

      metrics: [
        {
          label: "Incident Reduction",
          value: "58%",
          change: "+42%",
          trend: "up",
          icon: "TrendingDown",
        },
        {
          label: "Response Time",
          value: "-65%",
          change: "+55%",
          trend: "up",
          icon: "Clock",
        },
        {
          label: "False Alarms",
          value: "-72%",
          change: "+68%",
          trend: "up",
          icon: "AlertOff",
        },
        {
          label: "Operational Efficiency",
          value: "48%",
          change: "+35%",
          trend: "up",
          icon: "Zap",
        },
      ],

      caseStudies: [
        {
          id: "enterprise-cs-1",
          companyName: "TechCorp Global Headquarters",
          industry: "Technology Campus",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1e5539d-1764487569225.png",
          logoAlt: "TechCorp company logo with modern tech design elements",
          challenge: `TechCorp's 50-acre campus with multiple buildings faced challenges with perimeter security, unauthorized access, and inefficient security staff deployment. Traditional surveillance required constant manual monitoring and generated numerous false alarms.`,
          solution: `Implemented comprehensive security analytics across 500+ cameras with AI-powered threat detection, access control integration, and automated incident response workflows. Created centralized command center with intelligent alert prioritization.`,
          results: [
            {
              metric: "Security Incidents",
              value: "-62%",
              description: "Reduction in security breaches",
            },
            {
              metric: "False Alarms",
              value: "-78%",
              description: "Decrease in false alerts",
            },
            {
              metric: "Response Time",
              value: "-55%",
              description: "Faster incident response",
            },
          ],

          testimonialQuote: `The AI-powered analytics have revolutionized our security operations. We've significantly reduced incidents while improving our security team's efficiency and response capabilities.`,
          testimonialAuthor: "James Wilson",
          testimonialRole: "Chief Security Officer",
          image: "https://images.unsplash.com/photo-1628390707616-3940b65c686d",
          imageAlt:
            "Large modern corporate campus with multiple glass buildings and landscaped grounds",
        },
        {
          id: "enterprise-cs-2",
          companyName: "Manufacturing Excellence Inc",
          industry: "Industrial Manufacturing",
          logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1ba636f09-1764487570413.png",
          logoAlt:
            "Manufacturing Excellence company logo with industrial gear design",
          challenge: `Large manufacturing facility struggled with workplace safety compliance, asset tracking, and operational visibility across multiple production lines. Manual safety monitoring was inconsistent and reactive.`,
          solution: `Deployed integrated safety and operations analytics system with PPE compliance monitoring, hazard detection, and workflow optimization across the entire facility.`,
          results: [
            {
              metric: "Safety Incidents",
              value: "-71%",
              description: "Reduction in workplace accidents",
            },
            {
              metric: "Compliance",
              value: "+85%",
              description: "Improvement in safety compliance",
            },
            {
              metric: "Productivity",
              value: "+42%",
              description: "Increase in operational efficiency",
            },
          ],

          testimonialQuote: `The platform has transformed our approach to workplace safety and operations. We've created a safer work environment while significantly improving productivity and efficiency.`,
          testimonialAuthor: "Lisa Anderson",
          testimonialRole: "VP of Operations",
          image: "https://images.unsplash.com/photo-1703563210719-1c5f8586c6d6",
          imageAlt:
            "Modern manufacturing facility floor with automated equipment and workers in safety gear",
        },
      ],

      resources: [
        {
          id: "enterprise-wp-1",
          title: "Enterprise Security Best Practices",
          type: "whitepaper",
          description: "Comprehensive guide to AI-powered security systems",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "enterprise-guide-1",
          title: "Security Operations Playbook",
          type: "guide",
          description:
            "Implementation guide for intelligent security operations",
          downloadUrl: "#",
          icon: "BookOpen",
        },
        {
          id: "enterprise-checklist-1",
          title: "Compliance Requirements Checklist",
          type: "checklist",
          description: "Essential security and privacy compliance requirements",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
        {
          id: "enterprise-report-1",
          title: "Enterprise Security Trends Report",
          type: "report",
          description: "Latest insights on corporate security technology",
          downloadUrl: "#",
          icon: "BarChart3",
        },
      ],

      testimonial: {
        quote: `camlytix AI has become an integral part of our security infrastructure. The proactive threat detection has prevented numerous incidents and improved our overall security posture.`,
        author: "Amanda Foster",
        role: "Director of Corporate Security",
        company: "Global Enterprises Ltd",
        image:
          "https://img.rocket.new/generatedImages/rocket_gen_img_11974cf31-1763296445328.png",
        imageAlt:
          "Professional woman with blonde hair in business suit looking confident",
      },
      complianceInfo: {
        title: "Security & Privacy Standards",
        description:
          "Our enterprise security solutions meet the highest standards for data protection, privacy, and security compliance.",
        certifications: [
          {
            name: "ISO 27001",
            icon: "Shield",
            description: "Information security management certification",
          },
          {
            name: "SOC 2 Type II",
            icon: "FileCheck",
            description: "Security and availability controls verified",
          },
          {
            name: "GDPR Compliant",
            icon: "Award",
            description: "EU data protection regulation compliance",
          },
          {
            name: "HIPAA Ready",
            icon: "Lock",
            description: "Healthcare data protection capabilities",
          },
        ],

        regulations: [
          "End-to-end encryption for all data transmission and storage",
          "Role-based access control with comprehensive audit logging",
          "Privacy-preserving analytics with configurable data retention",
          "Regular security assessments and penetration testing",
          "Compliance with industry-specific regulations and standards",
        ],
      },
    },
    {
      id: "manufacturing",
      name: "Manufacturing & Operations",
      slug: "manufacturing",
      icon: "Factory",
      description: `Enhance factory safety, operational efficiency, and workforce productivity with AI-driven video intelligence. Our platform helps manufacturing plants detect hazards early, monitor workflows in real time, and ensure strict compliance with safety and operational standards.`,
      heroImage: "https://images.unsplash.com/photo-1679454690793-83340db232db",
      heroImageAlt:
        "Large manufacturing facility with machinery, workers, and safety monitoring systems",
      tagline: "Boost Operational Excellence with Intelligent Video Analytics",

      keyBenefits: [
        "Reduce workplace incidents by 40% with real-time hazard detection",
        "Improve operational efficiency by monitoring workflows and bottlenecks",
        "Strengthen compliance with automated safety and PPE alerts",
        "Track asset movement and prevent unauthorized access in restricted zones",
      ],

      useCases: [
        {
          id: "safety",
          title: "Worker Safety Monitoring",
          description:
            "Ensure safe operations with real-time detection of unsafe behaviors, PPE violations, and hazard zone intrusions.",
          icon: "ShieldAlert",
          features: [
            "PPE compliance checks",
            "Hazard zone breach alerts",
            "Slip & fall detection",
            "Safety violation reporting",
          ],
        },
        {
          id: "workflow",
          title: "Production Workflow Analytics",
          description:
            "Identify bottlenecks and optimize assembly line efficiency using intelligent activity tracking.",
          icon: "Workflow",
          features: [
            "Process cycle time analysis",
            "Bottleneck identification",
            "Idle worker/machine detection",
            "Task duration monitoring",
          ],
        },
        {
          id: "asset",
          title: "Asset & Vehicle Tracking",
          description:
            "Monitor forklifts, equipment, and sensitive materials across the facility in real time.",
          icon: "Truck",
          features: [
            "Forklift movement tracking",
            "Restricted zone access monitoring",
            "Asset flow analytics",
            "Equipment misuse detection",
          ],
        },
        {
          id: "compliance",
          title: "Regulatory Compliance",
          description:
            "Automate safety audits with continuous monitoring and digital documentation.",
          icon: "FileCheck",
          features: [
            "Automated compliance logs",
            "Audit-ready incident reports",
            "Video-based verification",
            "Custom policy enforcement",
          ],
        },
      ],

      metrics: [
        {
          label: "Safety Improvements",
          value: "42%",
          change: "+29%",
          trend: "up",
          icon: "Shield",
        },
        {
          label: "Operational Efficiency",
          value: "37%",
          change: "+21%",
          trend: "up",
          icon: "Zap",
        },
        {
          label: "Incident Reduction",
          value: "40%",
          change: "+32%",
          trend: "up",
          icon: "AlertTriangle",
        },
        {
          label: "Process Cycle Optimization",
          value: "28%",
          change: "+18%",
          trend: "up",
          icon: "TrendingUp",
        },
      ],

      caseStudies: [
        {
          id: "manu-cs-1",
          companyName: "SteelWorks Manufacturing",
          industry: "Heavy Industries",
          logo: "#",
          logoAlt: "SteelWorks logo",
          challenge: `SteelWorks faced recurring safety incidents and had little visibility into workflow inefficiencies across three facilities.`,
          solution: `Implemented real-time safety monitoring, vehicle tracking, and automated compliance reporting. Integrated alerts with the plant’s existing safety management system.`,
          results: [
            {
              metric: "Incident Reduction",
              value: "-45%",
              description: "Fewer on-site accidents",
            },
            {
              metric: "Efficiency Gain",
              value: "+33%",
              description: "Improved assembly line flow",
            },
            {
              metric: "Compliance Accuracy",
              value: "98%",
              description: "Automated regulatory reporting",
            },
          ],
          testimonialQuote: `We’ve never had this level of visibility into factory operations. camlytix AI made our workplace safer and more efficient.`,
          testimonialAuthor: "Rajesh Sharma",
          testimonialRole: "Plant Operations Head",
          image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e",
          imageAlt:
            "Factory interior with workers and automated safety monitoring systems",
        },
      ],

      resources: [
        {
          id: "manufacturing-wp-1",
          title: "AI in Manufacturing Safety",
          type: "whitepaper",
          description:
            "A deep dive into how AI prevents workplace risks and improves compliance.",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "manufacturing-checklist-1",
          title: "Safety Compliance Checklist",
          type: "checklist",
          description:
            "Ensure full alignment with safety protocols using this audit-ready checklist.",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
      ],

      testimonial: {
        quote: `With real-time hazard detection and workflow analytics, we improved productivity while making our facilities significantly safer.`,
        author: "Neha Verma",
        role: "Operations Manager",
        company: "Prime Manufacturing Co.",
        image: "#",
        imageAlt: "Professional woman in industrial environment",
      },

      complianceInfo: {
        title: "Safety & Compliance",
        description:
          "Our platform is designed for highly regulated industrial environments.",
        certifications: [
          {
            name: "ISO 45001",
            icon: "Award",
            description: "Occupational health & safety",
          },
          {
            name: "ISO 27001",
            icon: "Award",
            description: "Information security management",
          },
        ],
        regulations: [
          "Automated incident documentation",
          "Secure video processing for compliance verification",
          "Encrypted data storage for audit logs",
          "Configurable safety alert policies",
        ],
      },
    },
    {
      id: "education",
      name: "Education",
      slug: "education",
      icon: "GraduationCap",
      description: `Ensure a safer, smarter, and more efficient campus environment with AI-enabled video intelligence. Our platform helps schools, colleges, and universities prevent trespassing, monitor student activity flows, and improve overall campus security.`,
      heroImage: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f",
      heroImageAlt:
        "University campus entrance with students walking and security cameras monitoring activity",
      tagline: "Create Safer, Smarter Campuses with Video AI",

      keyBenefits: [
        "Reduce security risks with automated intrusion & loitering detection",
        "Monitor student activity and crowd patterns across large campuses",
        "Enhance campus operations with analytics-driven decision making",
        "Protect restricted areas such as labs, hostels, and server rooms",
      ],

      useCases: [
        {
          id: "campus-security",
          title: "Campus Security Intelligence",
          description:
            "Monitor school and campus grounds with real-time detection of threats, intrusions, and unusual behaviors.",
          icon: "Shield",
          features: [
            "Trespassing & intrusion alerts",
            "Perimeter monitoring",
            "Loitering detection",
            "Unusual activity analytics",
          ],
        },
        {
          id: "student-flow",
          title: "Student Movement Analytics",
          description:
            "Track crowd movement to improve safety, avoid congestion, and manage event-day traffic.",
          icon: "Users",
          features: [
            "Crowd density analytics",
            "Peak hour movement reports",
            "Dwell time measurement",
            "Cross-zone tracking",
          ],
        },
        {
          id: "attendance",
          title: "Smart Attendance Tracking",
          description:
            "Automate attendance and visitor logs using intelligent face & movement analytics.",
          icon: "UserCheck",
          features: [
            "Automated face-based attendance",
            "Visitor management",
            "Lab/hostel access logs",
            "Time-based campus entry insights",
          ],
        },
        {
          id: "asset-protection",
          title: "Asset & Facility Protection",
          description:
            "Secure laboratories, libraries, equipment rooms, and hostels using zone-based access rules.",
          icon: "Lock",
          features: [
            "Restricted zone access alerts",
            "Sensitive area protection",
            "Equipment room monitoring",
            "Night-time surveillance automation",
          ],
        },
      ],

      metrics: [
        {
          label: "Security Risk Reduction",
          value: "55%",
          change: "+38%",
          trend: "up",
          icon: "Shield",
        },
        {
          label: "Incident Response Speed",
          value: "3.5x",
          change: "+210%",
          trend: "up",
          icon: "Zap",
        },
        {
          label: "Operational Efficiency",
          value: "30%",
          change: "+18%",
          trend: "up",
          icon: "TrendingUp",
        },
        {
          label: "Attendance Accuracy",
          value: "97%",
          change: "+20%",
          trend: "up",
          icon: "UserCheck",
        },
      ],

      caseStudies: [
        {
          id: "edu-cs-1",
          companyName: "Greenfield University",
          industry: "Higher Education",
          logo: "#",
          logoAlt: "Greenfield University logo",
          challenge: `Greenfield University struggled with campus trespassing, unmanaged crowd flow during peak hours, and manual attendance processes.`,
          solution: `Deployed intrusion detection, movement analytics, and face-based attendance tracking across key campus zones.`,
          results: [
            {
              metric: "Trespassing Cases",
              value: "-72%",
              description: "Sharp decline in unauthorized entry",
            },
            {
              metric: "Attendance Accuracy",
              value: "97%",
              description: "Fully automated attendance system",
            },
            {
              metric: "Crowd Management",
              value: "45%",
              description: "Improved movement across campus",
            },
          ],
          testimonialQuote: `camlytix AI has made our campus safer and smarter. The automated insights have streamlined our operations significantly.`,
          testimonialAuthor: "Dr. R. Menon",
          testimonialRole: "Dean of Facilities",
          image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c",
          imageAlt: "University dean smiling in front of academic building",
        },
      ],

      resources: [
        {
          id: "edu-wp-1",
          title: "Campus Safety Framework",
          type: "whitepaper",
          description:
            "A complete guide to implementing AI for school and campus security.",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "edu-checklist-1",
          title: "Campus Security Checklist",
          type: "checklist",
          description:
            "Essential security measures for educational institutions.",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
      ],

      testimonial: {
        quote: `The real-time alerts and analytics have drastically improved our response time and campus visibility.`,
        author: "Sangeetha Rao",
        role: "Campus Security Director",
        company: "BlueRiver College",
        image: "#",
        imageAlt: "Professional woman in academic setting",
      },

      complianceInfo: {
        title: "Privacy & Compliance",
        description:
          "Built for secure educational environments with strict data protection.",
        certifications: [
          {
            name: "FERPA Aligned",
            icon: "Shield",
            description: "Student privacy compliance",
          },
          {
            name: "ISO 27001",
            icon: "Award",
            description: "Information security certification",
          },
        ],
        regulations: [
          "No biometric data stored unless institution-approved",
          "Encrypted video feeds and logs",
          "Strict access control for administrators",
          "Customizable retention policies for student privacy",
        ],
      },
    },
    {
      id: "logistics",
      name: "Logistics & Warehousing",
      slug: "logistics",
      icon: "Boxes",
      description: `Optimize warehouse operations, enhance worker safety, and streamline logistics with real-time video intelligence. Our platform helps logistics hubs track asset movement, prevent accidents, and maintain high levels of operational continuity.`,
      heroImage: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
      heroImageAlt:
        "Warehouse with forklifts, workers, and storage racks monitored through video analytics",
      tagline: "Intelligent Visibility for Modern Warehousing",

      keyBenefits: [
        "Prevent accidents with automated detection of high-risk activities",
        "Track movement of forklifts, pallets, and personnel in real time",
        "Monitor dock, loading bay, and cross-docking operations efficiently",
        "Reduce operational delays through workflow analytics",
      ],

      useCases: [
        {
          id: "forklift",
          title: "Forklift & Vehicle Tracking",
          description:
            "Track forklift and vehicle movements to avoid collisions, ensure safe driving, and optimize routing.",
          icon: "Truck",
          features: [
            "Route deviation detection",
            "Collision risk alerts",
            "Speed & movement tracking",
            "Unauthorized vehicle entry alerts",
          ],
        },
        {
          id: "inventory",
          title: "Inventory & Pallet Monitoring",
          description:
            "Monitor pallet movement, loading/unloading operations, and detect misplaced assets instantly.",
          icon: "PackageSearch",
          features: [
            "Real-time pallet tracking",
            "Cross-docking monitoring",
            "Misplaced inventory alerts",
            "Loading bay utilization analytics",
          ],
        },
        {
          id: "workflow",
          title: "Workflow Optimization",
          description:
            "Improve throughput by analyzing bottlenecks and worker activity across warehouse zones.",
          icon: "Workflow",
          features: [
            "Activity heatmaps",
            "Process bottleneck detection",
            "Worker movement insights",
            "Shift performance metrics",
          ],
        },
        {
          id: "safety",
          title: "Warehouse Safety",
          description:
            "Reduce incidents with continuous monitoring of safety rule violations and hazard zones.",
          icon: "AlertTriangle",
          features: [
            "Restricted zone breach alerts",
            "Unsafe behavior detection",
            "Slip & fall detection",
            "Night-time security monitoring",
          ],
        },
      ],

      metrics: [
        {
          label: "Incident Reduction",
          value: "47%",
          change: "+33%",
          trend: "up",
          icon: "Shield",
        },
        {
          label: "Operational Throughput",
          value: "32%",
          change: "+21%",
          trend: "up",
          icon: "TrendingUp",
        },
        {
          label: "Forklift Compliance",
          value: "89%",
          change: "+18%",
          trend: "up",
          icon: "CheckCircle",
        },
        {
          label: "Asset Tracking Accuracy",
          value: "96%",
          change: "+19%",
          trend: "up",
          icon: "PackageSearch",
        },
      ],

      caseStudies: [
        {
          id: "logistics-cs-1",
          companyName: "NorthStar Logistics",
          industry: "Supply Chain & Distribution",
          logo: "#",
          logoAlt: "NorthStar Logistics logo",
          challenge: `NorthStar struggled with frequent forklift collisions, misplaced pallets, and workflow delays across its distribution center.`,
          solution: `Deployed forklift tracking, pallet monitoring, and workflow optimization modules with automated incident alerts.`,
          results: [
            {
              metric: "Operational Throughput",
              value: "+38%",
              description: "Improved processing capacity",
            },
            {
              metric: "Accident Reduction",
              value: "-52%",
              description: "Fewer forklift-related incidents",
            },
            {
              metric: "Asset Tracking Accuracy",
              value: "98%",
              description: "Real-time pallet visibility",
            },
          ],
          testimonialQuote: `camlytix AI has significantly improved our warehouse safety and workflow. The insights unlocked new levels of efficiency.`,
          testimonialAuthor: "Rohan Patel",
          testimonialRole: "Warehouse Operations Manager",
          image: "https://images.unsplash.com/photo-1644584210247-e02144e284c4",
          imageAlt: "Logistics warehouse manager holding a digital device",
        },
      ],

      resources: [
        {
          id: "log-wp-1",
          title: "Warehouse Automation Guide",
          type: "whitepaper",
          description:
            "Learn how AI transforms logistics and warehouse efficiency.",
          downloadUrl: "#",
          icon: "FileText",
        },
        {
          id: "log-checklist-1",
          title: "Logistics Safety Checklist",
          type: "checklist",
          description:
            "Ensure safety and compliance across your warehouse operations.",
          downloadUrl: "#",
          icon: "CheckSquare",
        },
      ],

      testimonial: {
        quote: `The platform gave us complete visibility into movements, safety, and bottlenecks. It has become essential to our warehouse operations.`,
        author: "Abhishek Nair",
        role: "Logistics Director",
        company: "Express Freight Co.",
        image: "#",
        imageAlt: "Professional logistics director in warehouse office",
      },

      complianceInfo: {
        title: "Safety & Operational Compliance",
        description:
          "Designed to meet the strict standards of modern logistics operations.",
        certifications: [
          {
            name: "ISO 45001",
            icon: "Award",
            description: "Occupational safety compliance",
          },
          {
            name: "ISO 27001",
            icon: "Award",
            description: "Information security certification",
          },
        ],
        regulations: [
          "Secure handling of operational video data",
          "Incident reporting aligned with safety standards",
          "Encrypted processing for forklift and worker analytics",
          "Full audit trails for compliance and training",
        ],
      },
    },
  ];

  const currentIndustry =
    industries.find((ind) => ind.id === activeIndustry) || industries[0];

  const handleRequestDemo = () => {
    console.log("Demo requested for:", currentIndustry.name);
    alert(
      `Demo request submitted for ${currentIndustry.name}. Our team will contact you within 24 hours.`
    );
  };

  const handleCalculateROI = () => {
    setShowROICalculator(true);
  };

  const handleViewFullCase = (caseStudyId: string) => {
    console.log("Viewing case study:", caseStudyId);
    alert("Full case study would open in a new window or modal.");
  };

  const handleDownloadResource = (resourceId: string) => {
    console.log("Downloading resource:", resourceId);
    alert("Resource download initiated. Check your downloads folder.");
  };

  const handleContactSales = () => {
    console.log("Contact sales for:", currentIndustry.name);
    alert("Sales contact form would open here.");
  };

  const industryList = industries.map((ind) => ({
    id: ind.id,
    name: ind.name,
    icon: ind.icon,
  }));

  return (
    <>
      <Helmet>
        <title>
          Industry Applications - camlytix AI | AI-Powered Solutions
        </title>
        <meta
          name="description"
          content="Discover how camlytix AI transforms operations across retail, traffic management, and enterprise security with AI-powered video analytics."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <IndustrySelector
          industries={industryList}
          activeIndustry={activeIndustry}
          onSelectIndustry={setActiveIndustry}
        />

        <IndustryHero
          industry={currentIndustry}
          onRequestDemo={handleRequestDemo}
          onCalculateROI={handleCalculateROI}
        />

        <UseCaseGrid useCases={currentIndustry.useCases} />

        <MetricsShowcase
          metrics={currentIndustry.metrics}
          industryName={currentIndustry.name}
        />

        {/* 
        <CaseStudySection
          caseStudies={currentIndustry.caseStudies}
          onViewFullCase={handleViewFullCase} />


        <ResourceLibrary
          resources={currentIndustry.resources}
          onDownload={handleDownloadResource} />


        <ComplianceSection complianceInfo={currentIndustry.complianceInfo} /> */}

        <CTASection
          industryName={currentIndustry.name}
          onRequestDemo={handleRequestDemo}
          onContactSales={handleContactSales}
        />

        {showROICalculator && (
          <ROICalculator
            industryType={activeIndustry as "retail" | "traffic" | "enterprise"}
            onClose={() => setShowROICalculator(false)}
          />
        )}
      </div>
    </>
  );
};

export default IndustryApplications;
